import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@design-system/Card';
import { Skeleton } from '@design-system/Skeleton';
import { useDynamicConfig } from '@statsig/react-bindings';
import { cn } from '@/utils/styles';

import { useOrderTrackingViewModel } from '@/features/OrderTrackingViewModels';
import { analyticsEvent } from '@/utils/analyticsEvent';
import { EventTracker } from '@/utils';
import { OrderTrackingModal } from './components/OrderTrackingModal';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon.tsx';

export const AccountManagementPageOrderTrackingSection = () => {
  const config = useDynamicConfig('public_config');
  const isOrderTrackingEnabled = config.get('enable_order_tracking', false);

  const {
    isLoading,
    isError,
    hasOrder,
    progressPercentage,
    stageLabels,
    activeStep,
    orderInfo,
  } = useOrderTrackingViewModel();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('orderTracking') === 'true' && hasOrder) {
      setIsModalOpen(true);
      searchParams.delete('orderTracking');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, hasOrder, setSearchParams]);

  if (!isOrderTrackingEnabled) return null;

  if (isLoading) {
    return (
      <div className="flex flex-col" data-testid="orderTracking-skeleton">
        <Skeleton className="h-6 w-40 mb-6" />
        <Card className="border-0 bg-background-blur-bold">
          <CardContent className="flex flex-col gap-4 p-6">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-1 w-full rounded-full" />
            <div className="flex justify-between">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !hasOrder) return null;

  const handleTrackOrder = () => {
    EventTracker.trackEvent(
      'account',
      analyticsEvent.ORDER_TRACKING_SEE_UPDATES_TAP,
    );
    setIsModalOpen(true);
  };

  const handleOrderDetails = () => {
    EventTracker.trackEvent(
      'account',
      analyticsEvent.ORDER_TRACKING_ORDER_DETAILS_TAP,
    );
    if (orderInfo?.shopify_order_number) {
      navigate(`/account/order-detail/${orderInfo.shopify_order_number}`);
    }
  };

  return (
    <div className="flex flex-col" data-testid="orderTracking-section">
      <Card
        data-testid="orderTracking-card"
        className="border-0 bg-background-blur-bold"
      >
        <CardContent
          className="flex flex-col gap-4 p-6"
          data-testid="orderTracking-cardContent"
        >
          <span
            className="typography-h3 text-[#146464] mb-4"
            data-testid="orderTracking-cardTitle"
          >
            {activeStep?.header_text ?? 'Order Status'}
          </span>

          <div
            data-testid="orderTracking-progressBarContainer"
            className="w-full h-1 bg-background-secondary rounded-full relative"
          >
            <div
              data-testid="orderTracking-progressBar"
              className="h-full bg-background-success transition-all duration-300 ease-in-out"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <div
            className="flex justify-between"
            data-testid="orderTracking-stageLabels"
          >
            {stageLabels.map((label, index) => (
              <span
                key={label}
                className={cn(
                  'typography-caption2',
                  activeStep?.smp_status === label
                    ? 'text-primary font-medium'
                    : 'text-secondary',
                )}
                data-testid={`orderTracking-stageLabel-${index}`}
              >
                {label}
              </span>
            ))}
          </div>

          <div
            className="flex items-center justify-between cursor-pointer pt-2"
            onClick={handleTrackOrder}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleTrackOrder()}
            data-testid="orderTracking-trackOrderButton"
          >
            <span className="typography-body text-primary">Track my Order</span>
            <ChevronRightIcon
              className="ml-auto size-6 text-tertiary"
              aria-hidden
            />
          </div>
        </CardContent>
      </Card>

      <Card
        data-testid="orderDetails-card"
        className="mt-4 border-0 bg-background-blur-bold"
      >
        <CardContent
          className="flex flex-col gap-4 p-4"
          data-testid="orderDetails-cardContent"
        >
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={handleOrderDetails}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleOrderDetails()}
            data-testid="orderTracking-orderDetailsButton"
          >
            <span className="typography-body text-primary">Order details</span>
            <ChevronRightIcon
              className="ml-auto size-6 text-tertiary"
              aria-hidden
            />
          </div>
        </CardContent>
      </Card>

      <OrderTrackingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
